"use client";

import { PointerEvent, RefObject, RefCallback, SyntheticEvent, useCallback, useRef } from "react";

/** How far a touch has to travel before it counts as a drag rather than a tap. */
const DRAG_THRESHOLD = 8;

/**
 * Handlers for an arrow overlay that sits over the ends of a scroll row. Horizontal wheel and
 * trackpad scrolls and touch drags that start on the overlay scroll the row; taps still reach
 * the arrow, and a tap that turned into a drag doesn't click it.
 */
export default function useArrowGestures(scrollerRef: RefObject<HTMLElement | null>): ArrowGestures {
  const dragRef = useRef<{ pointerId: number; x: number; scrollLeft: number; dragging: boolean } | null>(null);
  const suppressClickRef = useRef(false);

  const wheelRef = useCallback<RefCallback<HTMLElement>>(
    (node) => {
      if (!node) {
        return;
      }
      const onWheel = (event: WheelEvent): void => {
        const scroller = scrollerRef.current;
        const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
        const delta = horizontal ? event.deltaX : event.shiftKey ? event.deltaY : 0;
        if (!scroller || delta === 0) {
          return;
        }
        event.preventDefault();
        scroller.scrollLeft += delta;
      };
      node.addEventListener("wheel", onWheel, { passive: false });
      return () => node.removeEventListener("wheel", onWheel);
    },
    [scrollerRef],
  );

  const onPointerDown = (event: PointerEvent<HTMLElement>): void => {
    suppressClickRef.current = false;
    const scroller = scrollerRef.current;
    if (!scroller || event.pointerType === "mouse") {
      return;
    }
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      scrollLeft: scroller.scrollLeft,
      dragging: false,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLElement>): void => {
    const drag = dragRef.current;
    const scroller = scrollerRef.current;
    if (!drag || !scroller || event.pointerId !== drag.pointerId) {
      return;
    }
    // dx is measured from where the touch started, so every move sets the row's position
    // outright. Small moves stay a tap until the finger passes the threshold once.
    const dx = event.clientX - drag.x;
    if (!drag.dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD) {
        return;
      }
      drag.dragging = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    scroller.scrollLeft = drag.scrollLeft - dx;
  };

  const onPointerEnd = (): void => {
    suppressClickRef.current = dragRef.current?.dragging ?? false;
    dragRef.current = null;
  };

  const onClickCapture = (event: SyntheticEvent): void => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return {
    ref: wheelRef,
    onPointerDown,
    onPointerMove,
    onPointerUp: onPointerEnd,
    onPointerCancel: onPointerEnd,
    onClickCapture,
  };
}

interface ArrowGestures {
  ref: RefCallback<HTMLElement>;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onClickCapture: (event: SyntheticEvent) => void;
}
